import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { api } from '../../config/api';
import { CONFIGURATIONS } from '../../config/envConfig';
import './courseRealtime.css';

const Context = createContext(null);
const idOf = message => String(message._id || message.id);
export function mergeCourseMessages(previous, incoming) {
  const merged = new Map(previous.map(message => [idOf(message), message]));
  incoming.forEach(message => merged.set(idOf(message), { ...merged.get(idOf(message)), ...message }));
  return [...merged.values()].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt) || idOf(a).localeCompare(idOf(b)));
}
export function CourseRealtimeProvider({ enabled, brand, children }) {
  const [socket, setSocket] = useState(null);
  useEffect(() => {
    if (!enabled || !localStorage.getItem('token')) return;
    const connection = io(`${(CONFIGURATIONS.API_BASE_DOMAIN || '').replace(/\/$/, '')}/courses`, {
      forceNew: true,
      auth: callback => callback({ token: localStorage.getItem('token'), brand }),
    });
    const changed = event => window.dispatchEvent(new CustomEvent('course-unread-changed', { detail: event }));
    connection.on('course:update', changed);
    const reconnect = () => changed({ kind: 'reconnect' });
    connection.on('connect', reconnect);
    setSocket(connection);
    // Also handles logout/token replacement in another tab without stale presence.
    const checkToken = () => {
      if (!localStorage.getItem('token')) connection.disconnect();
      else if (!connection.connected) connection.connect();
    };
    window.addEventListener('storage', checkToken);
    window.addEventListener('focus', checkToken);
    return () => {
      window.removeEventListener('storage', checkToken);
      window.removeEventListener('focus', checkToken);
      connection.off('course:update', changed); connection.off('connect', reconnect);
      connection.disconnect(); setSocket(null);
    };
  }, [enabled, brand]);
  return <Context.Provider value={socket}>{children}</Context.Provider>;
}

export function useCourseRealtime({ courseId, base, messages, historyStart, onMessages, onReceipts, onCourse }) {
  const socket = useContext(Context);
  const handlers = useRef({ messages, historyStart, onMessages, onReceipts, onCourse });
  handlers.current = { messages, onMessages, onReceipts, onCourse };
  const [presence, setPresence] = useState(null);
  const [typing, setTyping] = useState([]);
  const [connected, setConnected] = useState(false);
  const [syncError, setSyncError] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const lastTyping = useRef(0);
  const typingTimer = useRef(null);
  useEffect(() => {
    let disposed = false;
    let recovering = false;
    let syncCursor = historyStart?.cursor;
    let recoveryQueued = false;
    let receiptRunning = false;
    let receiptQueued = false;
    setPresence(null); setTyping([]); setConnected(false); setUnavailable(false);
    const applyMessages = items => { if (!disposed) handlers.current.onMessages(items); };
    const receiptRefresh = async () => {
      if (receiptRunning) { receiptQueued = true; return; }
      receiptRunning = true;
      try {
        do {
          receiptQueued = false;
          const ids = handlers.current.messages.map(idOf);
          for (let offset = 0; offset < ids.length && !disposed; offset += 50) {
            const response = await api.get(`${base}/messages/receipts?ids=${ids.slice(offset, offset + 50).join(',')}`);
            if (!disposed) handlers.current.onReceipts(response.data.data);
          }
        } while (receiptQueued && !disposed);
      } catch (_) { /* Retried at the next update/reconnect. */ }
      finally { receiptRunning = false; }
    };
    const recover = async (full = true) => {
      // Wait for the initial history snapshot before recovering missed arrivals.
      if (historyStart === null || (historyStart && historyStart.courseId !== courseId)) return;
      if (recovering) { recoveryQueued = true; return; }
      recovering = true;
      try {
        do {
          recoveryQueued = false;
          if (Number.isSafeInteger(syncCursor)) {
            let hasMore;
            do {
              const response = await api.get(`${base}/messages?limit=50&afterSequence=${syncCursor}`);
              if (disposed) return;
              const data = response.data.data;
              if (!Number.isSafeInteger(data.syncCursor) || data.syncCursor < syncCursor || (data.hasMore && data.syncCursor === syncCursor)) throw new Error('Invalid course sync response');
              applyMessages(data.messages);
              syncCursor = data.syncCursor;
              hasMore = data.hasMore;
            } while (hasMore && !disposed);
          } else {
            // Compatibility during deployment against the previous API version.
            let before = null;
            do {
              const response = await api.get(`${base}/messages?limit=50${before ? `&before=${before}` : ''}`);
              if (disposed) return;
              applyMessages(response.data.data.messages);
              before = response.data.data.nextBefore;
            } while (full && before && !disposed);
          }
          const detail = await api.get(base);
          if (!disposed) { handlers.current.onCourse(detail.data.data); setSyncError(false); }
          await receiptRefresh();
        } while (recoveryQueued && !disposed);
      } catch (_) { if (!disposed) setSyncError(true); }
      finally { recovering = false; }
    };
    const join = () => {
      socket.emit('course:join', { courseId }, result => {
        if (disposed) return;
        setConnected(Boolean(result?.ok)); setUnavailable(!result?.ok);
        if (result?.ok) recover(handlers.current.messages.length > 0);
      });
    };
    const update = async event => {
      if (event.courseId !== courseId || event.kind === 'notifications') return;
      if (event.kind === 'read') { receiptRefresh(); return; }
      if (event.kind !== 'message' || !event.messageId) { recover(false); return; }
      try {
        const response = await api.get(`${base}/messages/${event.messageId}`);
        applyMessages([response.data.data]);
      } catch (_) { if (!disposed) { setSyncError(true); recover(false); } }
    };
    const lost = () => { setConnected(false); setPresence(null); setTyping([]); };
    const online = data => { if (data.courseId === courseId) setPresence(data); };
    const typingChanged = data => { if (data.courseId === courseId) setTyping(data.roles || []); };
    const revoked = data => { if (data.courseId === courseId) { lost(); setUnavailable(true); } };
    const focused = () => { if (document.visibilityState !== 'hidden') { if (socket?.connected) join(); else recover(true); } };
    if (socket) {
      socket.on('connect', join); socket.on('disconnect', lost); socket.on('connect_error', lost);
      socket.on('course:update', update); socket.on('course:presence', online);
      socket.on('course:typing', typingChanged); socket.on('course:unavailable', revoked);
      if (socket.connected) join();
    }
    window.addEventListener('focus', focused);
    document.addEventListener('visibilitychange', focused);
    const fallback = setInterval(() => {
      if (document.visibilityState !== 'hidden') recover(false);
    }, 30000);
    return () => {
      disposed = true; clearInterval(fallback); clearTimeout(typingTimer.current);
      window.removeEventListener('focus', focused); document.removeEventListener('visibilitychange', focused);
      if (socket) {
        socket.emit('course:leave'); socket.off('connect', join); socket.off('disconnect', lost); socket.off('connect_error', lost);
        socket.off('course:update', update); socket.off('course:presence', online);
        socket.off('course:typing', typingChanged); socket.off('course:unavailable', revoked);
      }
    };
  }, [socket, base, courseId, historyStart]);
  const setTypingState = useCallback(active => {
    clearTimeout(typingTimer.current);
    if (!socket?.connected) return;
    if (!active || Date.now() - lastTyping.current >= 1500) {
      socket.emit('course:typing', { courseId, typing: Boolean(active) });
      lastTyping.current = Date.now();
    }
    if (active) typingTimer.current = setTimeout(() => socket.emit('course:typing', { courseId, typing: false }), 3000);
  }, [socket, courseId]);
  useEffect(() => {
    const stop = () => setTypingState(false);
    window.addEventListener('blur', stop); document.addEventListener('visibilitychange', stop);
    return () => { window.removeEventListener('blur', stop); document.removeEventListener('visibilitychange', stop); };
  }, [setTypingState]);
  return { connected, presence, typing, setTyping: setTypingState, syncError, unavailable };
}

// Read means the incoming message actually entered the visible conversation viewport.
export function useCourseViewport({ base, messages, enabled = true }) {
  const [node, setNode] = useState(null);
  const seen = useRef(new Set());
  const nearBottom = useRef(true);
  const previous = useRef({ first: null, last: null, height: 0, anchor: null });
  const [newMessages, setNewMessages] = useState(false);
  const [awayFromBottom, setAwayFromBottom] = useState(false);
  useEffect(() => { seen.current = new Set(); previous.current = { first: null, last: null, height: 0, anchor: null }; nearBottom.current = true; setNewMessages(false); setAwayFromBottom(false); }, [base]);
  const rememberAnchor = useCallback(() => {
    if (!node) return null;
    const top = node.getBoundingClientRect().top;
    const element = [...node.querySelectorAll('[data-course-message]')].find(item => item.getBoundingClientRect().bottom > top);
    return element ? { id: element.dataset.courseMessage, offset: element.getBoundingClientRect().top - top } : null;
  }, [node]);
  const bottom = useCallback(() => { if (node) node.scrollTop = node.scrollHeight; nearBottom.current = true; setNewMessages(false); setAwayFromBottom(false); }, [node]);
  useLayoutEffect(() => {
    if (!node || !enabled) return;
    const first = messages.length ? idOf(messages[0]) : null;
    const last = messages.length ? idOf(messages[messages.length - 1]) : null;
    const prepended = first && previous.current.first && first !== previous.current.first;
    const appended = last !== previous.current.last;
    const latest = [...node.querySelectorAll('[data-course-message]')].pop();
    if (appended && latest?.dataset.courseIncoming === 'false') bottom();
    else if (prepended || !nearBottom.current) {
      const anchor = previous.current.anchor;
      const element = anchor && [...node.querySelectorAll('[data-course-message]')].find(item => item.dataset.courseMessage === anchor.id);
      if (element) node.scrollTop += element.getBoundingClientRect().top - node.getBoundingClientRect().top - anchor.offset;
      else if (prepended && !appended) node.scrollTop += node.scrollHeight - previous.current.height;
      if (appended) setNewMessages(true);
    } else if (appended) bottom();
    previous.current = { first, last, height: node.scrollHeight, anchor: rememberAnchor() };
  }, [messages, node, bottom, enabled, rememberAnchor]);
  useEffect(() => {
    if (!node || !enabled) return;
    let disposed = false;
    let timer;
    let pending = new Set();
    let running = false;
    const visible = new Set();
    const flush = async () => {
      if (running || disposed || document.visibilityState === 'hidden' || !document.hasFocus()) return;
      const ids = [...pending].filter(id => !seen.current.has(id)).slice(0, 50);
      if (!ids.length) return;
      running = true;
      try {
        await api.post(`${base}/messages/read`, { messageIds: ids });
        if (disposed) return;
        ids.forEach(id => { seen.current.add(id); pending.delete(id); });
        window.dispatchEvent(new Event('course-unread-changed'));
      } catch (_) { /* Retry while messages remain visible. */ }
      finally { running = false; if (!disposed && pending.size) timer = setTimeout(flush, 3000); }
    };
    const queue = () => {
      if (document.visibilityState === 'hidden' || !document.hasFocus()) return;
      visible.forEach(id => { if (!seen.current.has(id)) pending.add(id); });
      clearTimeout(timer); timer = setTimeout(flush, 150);
    };
    const observer = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(entries => {
      entries.forEach(entry => { const id = entry.target.dataset.courseMessage; if (entry.isIntersecting && entry.intersectionRatio !== 0) visible.add(id); else { visible.delete(id); pending.delete(id); } }); queue();
    }, { root: null, threshold: 0.01 }) : null;
    const elements = [...node.querySelectorAll('[data-course-incoming="true"]')];
    const measure = () => {
      if (!observer) {
        const frame = node.getBoundingClientRect();
        elements.forEach(element => {
          const box = element.getBoundingClientRect(); const id = element.dataset.courseMessage;
          if (box.height > 0 && box.bottom > Math.max(frame.top, 0) && box.top < Math.min(frame.bottom, window.innerHeight)) visible.add(id);
          else { visible.delete(id); pending.delete(id); }
        });
      }
      queue();
    };
    elements.forEach(element => observer?.observe(element));
    measure(); node.addEventListener('scroll', measure); window.addEventListener('focus', measure); document.addEventListener('visibilitychange', measure);
    return () => { disposed = true; clearTimeout(timer); observer?.disconnect(); node.removeEventListener('scroll', measure); window.removeEventListener('focus', measure); document.removeEventListener('visibilitychange', measure); };
  }, [node, messages, base, enabled]);
  return { ref: setNode, onScroll: () => { if (node) { nearBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 100; setAwayFromBottom(!nearBottom.current); if (nearBottom.current) setNewMessages(false); previous.current.anchor = rememberAnchor(); previous.current.height = node.scrollHeight; } }, newMessages, awayFromBottom, bottom };
}
const labels = { customer: 'Customer', writer: 'Writer', admin: 'Support' };
export function CourseLiveStatus({ live, role }) {
  if (!live) return null;
  if (live.unavailable) return <p className="course-live-status" role="status">Course access changed. Please return to your course list.</p>;
  return <div className="course-live-status" aria-live="polite">
    {!live.connected ? <span>Connecting to live updates…</span> : <span>{Object.entries(labels).filter(([key]) => key !== role && live.presence?.[key] !== null && live.presence?.[key] !== undefined).map(([key, label]) => <span className="course-presence-person" key={key}><i className={live.presence[key] ? 'course-presence-online' : ''} aria-hidden="true" />{label} {live.presence[key] ? 'online' : 'offline'}</span>)}</span>}
    {live.syncError && <span> Sync interrupted. Retrying…</span>}
  </div>;
}
// A zero-height anchor above the composer keeps typing outside the scrolling history.
export function CourseTypingIndicator({ live }) {
  const people = live?.connected && !live.unavailable
    ? [...new Set((live.typing || []).map(role => labels[role]).filter(Boolean))] : [];
  return <div className="course-typing-overlay" role="status" aria-live="polite" aria-atomic="true">
    {!!people.length && <span className="course-typing-bubble">{people.join(', ')} {people.length === 1 ? 'is' : 'are'} typing…</span>}
  </div>;
}
export function CourseReadReceipt({ readBy = [] }) {
  const readers = [...new Set(readBy.map(item => labels[item.role]).filter(Boolean))];
  const seen = readers.length > 0;
  return <small className={`course-read-receipt${seen ? ' course-read-seen' : ''}`}><span className="course-receipt-checks" aria-hidden="true">{seen ? '✓✓' : '✓'}</span><span>{seen ? `Read by ${readers.join(', ')}` : 'Sent'}</span></small>;
}
export function CourseNewMessages({ viewport }) {
  return viewport.newMessages || viewport.awayFromBottom ? <button type="button" className="course-new-messages" onClick={viewport.bottom}>{viewport.newMessages ? "New messages ↓" : "Jump to latest ↓"}</button> : null;
}

// Use the same local calendar day as the displayed message timestamps.
export function CourseMessageDate({ message, previous, timeZone, as: Tag = 'div' }) {
  let formatter;
  try { formatter = new Intl.DateTimeFormat(undefined, { timeZone, year: 'numeric', month: 'short', day: 'numeric' }); }
  catch (_) { formatter = new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  const date = new Date(message.createdAt);
  if (!message.createdAt || Number.isNaN(date.getTime())) return null;
  const label = formatter.format(date);
  const preceding = previous && new Date(previous.createdAt);
  if (preceding && !Number.isNaN(preceding.getTime()) && formatter.format(preceding) === label) return null;
  return <Tag className="course-message-date"><span>{label}</span></Tag>;
}
